import {
  deleteDoc as dbDeleteDoc,
  getDoc as dbGetDoc,
  getDocs as dbGetDocs,
  setDoc as dbSetDoc,
  updateDoc as dbUpdateDoc,
  writeBatch,
  type DocumentData,
  type DocumentReference,
  type Query,
  type CollectionReference,
} from '../db/rethink-firestore';
import { col, type CollectionName } from '../collections';
import { utcNowIso } from '../format';

export type DocWithId<T = any> = T & { id: string };

function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `doc_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function ref<T extends DocumentData = any>(
  collectionName: CollectionName,
  id: string,
): DocumentReference<T> {
  return col<T>(collectionName).client.doc<T>(collectionName, id);
}

/** Create a document with a client-generated id + timestamps. */
export async function createDoc<T extends DocumentData = any>(
  collectionName: CollectionName,
  data: Partial<T>,
): Promise<DocWithId<T>> {
  const id = newId();
  const now = utcNowIso();
  const doc = { ...data, id, createdAt: now, updatedAt: now } as DocumentData;
  await dbSetDoc(ref(collectionName, id), doc);
  return doc as DocWithId<T>;
}

export async function updateDoc<T extends DocumentData = any>(
  collectionName: CollectionName,
  id: string,
  patch: Partial<T>,
): Promise<void> {
  await dbUpdateDoc(ref<T>(collectionName, id), { ...patch, updatedAt: utcNowIso() });
}

export async function setDocData<T extends DocumentData = any>(
  collectionName: CollectionName,
  id: string,
  data: Partial<T>,
  opts: { merge?: boolean } = {},
): Promise<void> {
  await dbSetDoc(ref<T>(collectionName, id), data as DocumentData, opts);
}

export async function deleteDoc(collectionName: CollectionName, id: string): Promise<void> {
  await dbDeleteDoc(ref(collectionName, id));
}

export async function getById<T extends DocumentData = any>(
  collectionName: CollectionName,
  id: string,
): Promise<T | undefined> {
  const snap = await dbGetDoc(ref<T>(collectionName, id));
  return snap.exists ? (snap.data() as T) : undefined;
}

export async function fetchQuery<T extends DocumentData = any>(
  queryInstance: Query<T> | CollectionReference<T>,
): Promise<DocWithId<T>[]> {
  const snap = await dbGetDocs(queryInstance as unknown as Query);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as DocWithId<T>);
}

export function batch() {
  return writeBatch(undefined);
}
